
import {defineComponent} from 'vue';
import {generateUUIDv4} from "@/helpers/functions/string";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {DocumentRestService} from "@/services/rest/master/DocumentRestService";

export default defineComponent({
  name: "InputFile",
  emits: ["update:modelValue", "fileSelected", "fileUploaded", "fileCleared", "exceptions"],
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      required: false,
      default: () => generateUUIDv4('file-input-'),
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    modelValue: {
      type: String,
      default: '',
    },
    errors: {
      type: Object,
      required: true,
    },
    rules: {
      type: String,
      required: false,
      default: '',
    },
    allowedExtensions: {
      type: Array as () => string[],
      required: false,
      default: null,
    },
    maxSizeMB: {
      type: Number,
      required: false,
      default: 10,
    },
    module: {
      type: String,
      required: false,
      default: DpModuleEnum.CMS,
    },
    useSignedUrl: {
      type: Boolean,
      required: false,
      default: false
    },
    typeCode: {
      type: String,
      required: true,
    },
    canBeCleared: {
      type: Boolean,
      required: false,
      default: true,
    },
    help: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      selectedFile: null as File | null,
      isUploading: false as boolean,
    }
  },
  computed: {
    acceptAttribute(): string {
      if (!this.allowedExtensions || this.allowedExtensions.length === 0) {
        return '';
      }

      // Convert extensions to MIME types and file extensions
      const acceptValues: string[] = [];

      this.allowedExtensions.forEach(ext => {
        const lowerExt = ext.toLowerCase();

        // Add the file extension format
        acceptValues.push(`.${lowerExt}`);

        // Add common MIME types for known extensions
        switch (lowerExt) {
          case 'csv':
            acceptValues.push('text/csv', 'application/csv');
            break;
          case 'pdf':
            acceptValues.push('application/pdf');
            break;
          case 'doc':
            acceptValues.push('application/msword');
            break;
          case 'docx':
            acceptValues.push('application/vnd.openxmlformats-officedocument.wordprocessingml.document');
            break;
          case 'xls':
            acceptValues.push('application/vnd.ms-excel');
            break;
          case 'xlsx':
            acceptValues.push('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            break;
          case 'txt':
            acceptValues.push('text/plain');
            break;
          case 'jpg':
          case 'jpeg':
            acceptValues.push('image/jpeg');
            break;
          case 'png':
            acceptValues.push('image/png');
            break;
          case 'gif':
            acceptValues.push('image/gif');
            break;
        }
      });

      return [...new Set(acceptValues)].join(',');
    },
  },
  watch: {
    modelValue: {
      handler(newValue: string) {
        if (!newValue) {
          this.selectedFile = null;
          this.clearFileInput();
        }
      },
      immediate: true,
    }
  },
  methods: {
    updateValue(value: string) {
      this.$emit('update:modelValue', value);
    },

    async handleFileSelection(event: Event, setFieldError: Function) {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0] || null;

      if (!file) {
        this.selectedFile = null;
        this.updateValue('');
        this.$emit('fileSelected', null);
        return;
      }

      // Validate file size
      if (file.size > this.maxSizeMB * 1024 * 1024) {
        const errorMessage = this.$t('uploadFileSizeTooBig', {maxSize: this.maxSizeMB});
        setFieldError(errorMessage);
        target.value = '';
        return;
      }

      // Validate the file extension if a value for allowedExtensions is provided
      if (this.allowedExtensions && this.allowedExtensions.length > 0) {
        const fileExtension = this.getFileExtension(file.name);
        const isAllowed = this.allowedExtensions.some(ext =>
          ext.toLowerCase() === fileExtension.toLowerCase()
        );

        if (!isAllowed) {
          const errorMessage = this.$t('invalidFileExtension', {
            allowed: this.allowedExtensions.join(', ')
          });
          setFieldError(errorMessage);
          target.value = '';
          return;
        }
      }

      this.selectedFile = file;
      this.$emit('fileSelected', file);

      // Upload the file immediately
      await this.uploadFile();
    },

    async uploadFile() {
      if (!this.selectedFile) {
        return;
      }

      this.isUploading = true;

      try {
        const documentKey: string | null =
          await DocumentRestService.getInstance().uploadFile(this.typeCode, this.selectedFile, this.module);

        if (documentKey) {
          this.updateValue(documentKey);
          this.$emit('fileUploaded', documentKey);
          return;
        }

        this.$emit('exceptions', this.$t('uploadFailed'));
      } catch (error: any) {
        console.error('File upload error:', error);
        this.$emit('exceptions', error);
        this.clearFileInput();
      } finally {
        this.isUploading = false;
      }
    },

    clearSelection() {
      const confirmed = window.confirm(this.$t('deleteConfirmation'));
      if (!confirmed) {
        return;
      }

      this.selectedFile = null;
      this.clearFileInput();

      this.updateValue('');
      this.$emit('fileCleared');
    },

    clearFileInput() {
      const fileInput = this.$refs.fileInput as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    },

    getFileExtension(filename: string): string {
      const lastDotIndex = filename.lastIndexOf('.');
      if (lastDotIndex === -1) {
        return ''; // No extension found
      }
      return filename.substring(lastDotIndex + 1);
    },

    formatFileSize(bytes: number): string {
      if (bytes === 0) return '0 Bytes';

      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
  },
});
